import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Camera } from "lucide-react";
import api from "@/lib/api";
import { LoadingState, EmptyState } from "@/components/ui/States";
import { URGENCY_COLOR, URGENCY_LABEL, formatPhone, timeAgo } from "@/lib/leadFormat";

// Lead-source slugs → friendly labels + an icon (lucide, already used in app).
const SOURCE_META = {
    "website-form": { label: "Free Estimate form", Icon: FileText },
    "upload-photos": { label: "Photo upload form", Icon: Camera },
};

const TEST_FILTERS = [
    { id: "real-only", label: "Hide TEST leads", hint: "Show only real customers (default)" },
    { id: "all", label: "Show all", hint: "Show real + TEST · DRY-RUN leads" },
    { id: "test-only", label: "Show only TEST leads", hint: "Tester Week sandbox" },
];

function LeadCard({ lead }) {
    const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
    const tagClass = URGENCY_COLOR[lead.urgency_tag] || "bg-[var(--color-line)]";
    const friendlyUrgency = URGENCY_LABEL[lead.urgency_tag] || lead.urgency_tag;
    const sourceMeta = SOURCE_META[lead.lead_source];
    const SourceIcon = sourceMeta?.Icon;
    const sourceLabel = sourceMeta?.label || lead.lead_source;
    const formattedPhone = formatPhone(lead.phone);
    return (
        <Link
            to={`/admin/leads/${lead.id}`}
            className="a1-card a1-card-link no-underline flex flex-col gap-2 hover:shadow"
            data-testid={`admin-lead-card-${lead.id}`}
            style={lead.is_test_seed ? { borderColor: "var(--color-storm)", borderWidth: 1.5 } : undefined}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        className={`a1-badge ${tagClass}`}
                        title={lead.urgency_tag}
                        data-testid={`urgency-badge-${lead.id}`}
                    >
                        {friendlyUrgency}
                    </span>
                    {lead.is_test_seed ? (
                        <span
                            className="a1-badge"
                            style={{ background: "rgba(231, 119, 78, 0.18)", color: "var(--color-storm)", border: "1px solid var(--color-storm)" }}
                            data-testid={`test-seed-badge-${lead.id}`}
                            title="Tester Week dry-run sample — not a real customer"
                        >
                            TEST · DRY-RUN
                        </span>
                    ) : null}
                </div>
                <span className="text-xs text-[var(--color-text-muted)]">{timeAgo(lead.created_at)}</span>
            </div>
            <div>
                <p className="font-semibold">{lead.name || "(no name)"} — {lead.city}</p>
                <p className="text-sm text-[var(--color-text-muted)]">{formattedPhone}</p>
            </div>
            <p className="text-sm text-[var(--color-text-muted)] flex items-center gap-1.5 flex-wrap">
                <span>{lead.service_needed || "service unspecified"}</span>
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1" title={`source: ${lead.lead_source}`}>
                    {SourceIcon ? <SourceIcon size={12} className="opacity-70" /> : null}
                    {sourceLabel}
                </span>
            </p>
            {isMobile ? (
                <div className="grid grid-cols-3 gap-1 mt-2">
                    <a href={`tel:${lead.phone}`} className="a1-btn-secondary text-xs py-2" onClick={(e) => e.stopPropagation()}>📞 Call</a>
                    <a href={`sms:${lead.phone}`} className="a1-btn-secondary text-xs py-2" onClick={(e) => e.stopPropagation()}>💬 Text</a>
                    <Link to={`/admin/leads/${lead.id}`} className="a1-btn-primary text-xs py-2">More</Link>
                </div>
            ) : null}
        </Link>
    );
}

export default function LeadList() {
    const [allLeads, setAllLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("real-only");

    useEffect(() => {
        api.get("/admin/leads").then((r) => setAllLeads(r.data.leads || [])).finally(() => setLoading(false));
    }, []);

    const counts = useMemo(() => {
        const test = allLeads.filter((l) => l.is_test_seed).length;
        const real = allLeads.length - test;
        return { test, real, total: allLeads.length };
    }, [allLeads]);

    const leads = useMemo(() => {
        if (filter === "test-only") return allLeads.filter((l) => l.is_test_seed);
        if (filter === "all") return allLeads;
        return allLeads.filter((l) => !l.is_test_seed);
    }, [allLeads, filter]);

    if (loading) return <LoadingState />;

    const stormQueue = leads.filter((l) => l.site_mode === "storm" && !["closed-won", "closed-lost"].includes(l.status));
    const active = leads.filter((l) => !stormQueue.includes(l) && ["new", "contacted"].includes(l.status));
    const recent = leads.filter((l) => l.status === "contacted" || l.status === "quoted").slice(0, 20);

    return (
        <div data-testid="admin-lead-list">
            <h1 className="mb-3">Leads</h1>
            <div className="a1-card mb-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid="lead-list-test-filter">
                <p className="text-xs uppercase tracking-[0.16em] text-[var(--color-text-muted)] m-0 shrink-0">View</p>
                <div className="flex flex-wrap gap-2 flex-1" role="radiogroup" aria-label="Lead filter">
                    {TEST_FILTERS.map((opt) => {
                        const active = filter === opt.id;
                        const isTestOnly = opt.id === "test-only";
                        return (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => setFilter(opt.id)}
                                role="radio"
                                aria-checked={active}
                                className="a1-badge text-sm"
                                title={opt.hint}
                                data-testid={`lead-filter-${opt.id}`}
                                style={{
                                    cursor: "pointer",
                                    background: active
                                        ? (isTestOnly ? "var(--color-storm)" : "var(--color-accent)")
                                        : "var(--color-bg-card)",
                                    color: active ? "var(--color-cta-ink)" : "var(--color-text)",
                                    border: `1px solid ${active
                                        ? (isTestOnly ? "var(--color-storm)" : "var(--color-accent)")
                                        : "var(--color-line)"}`,
                                }}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
                <p className="text-xs text-[var(--color-text-muted)] m-0 sm:text-right">
                    <strong>{counts.real}</strong> real · <strong style={{ color: "var(--color-storm)" }}>{counts.test}</strong> TEST · {counts.total} total
                </p>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
                <Column title="Storm Queue" leads={stormQueue} testId="admin-storm-queue" emptyHeadline="No storm leads in queue." />
                <Column title="Active" leads={active} testId="admin-active-queue" emptyHeadline={filter === "test-only" ? "No TEST leads — seed some from /admin/tester-week." : "No active leads — quiet morning."} />
                <Column title="Recently Contacted" leads={recent} testId="admin-recent-queue" emptyHeadline="Nothing recent." />
            </div>
        </div>
    );
}

function Column({ title, leads, testId, emptyHeadline }) {
    return (
        <div data-testid={testId}>
            <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold m-0" style={{ color: "var(--color-text)" }}>{title}</h2>
                <span
                    className="a1-badge text-xs"
                    style={{
                        background: "var(--color-bg-card)",
                        color: "var(--color-text-muted)",
                        border: "1px solid var(--color-line)",
                    }}
                >
                    {leads.length}
                </span>
            </div>
            {leads.length === 0 ? (
                <EmptyState headline={emptyHeadline} />
            ) : (
                <div className="grid gap-3">
                    {leads.map((l) => <LeadCard key={l.id} lead={l} />)}
                </div>
            )}
        </div>
    );
}
